type Listener = () => void;

type RadarFetchState = "idle" | "fetching" | "ok" | "failed" | "no-url";

export type RadarFetchStatus = {
  fetchState: RadarFetchState;
  lastError: string | null;
  attemptCount: number;
  lastAttemptAt: number | null;
};

const IDLE_STATUS: RadarFetchStatus = {
  fetchState: "idle",
  lastError: null,
  attemptCount: 0,
  lastAttemptAt: null,
};

/**
 * Runtime, memory-only fetch status per radar id (system-config fetch
 * lifecycle, retry backoff bookkeeping). Not persisted — a reload
 * restarts each radar's retry cycle clean. Consumers read by radar id
 * via useSyncExternalStore (see useRadarFetchStatus).
 */
export class RadarFetchStateStore {
  private statusByRadarId = new Map<string, RadarFetchStatus>();
  private listeners = new Map<string, Set<Listener>>();

  getStatus(radarId: string): RadarFetchStatus {
    return this.statusByRadarId.get(radarId) ?? IDLE_STATUS;
  }

  setFetching(radarId: string): void {
    this.setStatus(radarId, {
      ...this.getStatus(radarId),
      fetchState: "fetching",
    });
  }

  setOk(radarId: string): void {
    this.setStatus(radarId, {
      fetchState: "ok",
      lastError: null,
      attemptCount: 0,
      lastAttemptAt: Date.now(),
    });
  }

  setFailed(radarId: string, error: string): void {
    const current = this.getStatus(radarId);
    this.setStatus(radarId, {
      fetchState: "failed",
      lastError: error,
      attemptCount: current.attemptCount + 1,
      lastAttemptAt: Date.now(),
    });
  }

  /**
   * Permanently unretryable — no connection URL to fetch from. Distinct
   * from "failed" so retry scheduling excludes it; only a config change
   * (e.g. a fleet sync supplying a URL) can move a radar out of this state.
   */
  setNoUrl(radarId: string): void {
    this.setStatus(radarId, {
      fetchState: "no-url",
      lastError: "no-url",
      attemptCount: this.getStatus(radarId).attemptCount,
      lastAttemptAt: Date.now(),
    });
  }

  clear(radarId: string): void {
    this.statusByRadarId.delete(radarId);
    this.notify(radarId);
  }

  subscribe(radarId: string, listener: Listener): () => void {
    if (!this.listeners.has(radarId)) {
      this.listeners.set(radarId, new Set());
    }
    this.listeners.get(radarId)!.add(listener);

    return () => {
      const set = this.listeners.get(radarId);
      if (!set) {
        return;
      }
      set.delete(listener);
      if (set.size === 0) {
        this.listeners.delete(radarId);
      }
    };
  }

  private setStatus(radarId: string, status: RadarFetchStatus): void {
    this.statusByRadarId.set(radarId, status);
    this.notify(radarId);
  }

  private notify(radarId: string): void {
    this.listeners.get(radarId)?.forEach((listener) => listener());
  }
}

export const radarFetchStateStore = new RadarFetchStateStore();
